import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';
import { useEventCapacity } from '../../hooks/useEventCapacity';
import { useTicketRegistration } from '../../hooks/useTicketRegistration';

const EVENT_SLUG = 'galette-des-rois-2026';

export default function RSVPSection() {
  const [formData, setFormData] = useState({
    lastName: '',
    email: '',
    adults: 1,
    children: 0,
  });

  const { isAtCapacity, loading: capacityLoading } = useEventCapacity(EVENT_SLUG);
  const { register, reset, isLoading, error, ticket, isSuccess } = useTicketRegistration(EVENT_SLUG);

  const adjustCount = (type, change) => {
    setFormData((prev) => {
      const newCount = prev[type] + change;
      if (type === 'adults') {
        return { ...prev, [type]: Math.max(1, newCount) };
      }
      return { ...prev, [type]: Math.max(0, newCount) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await register(formData);
  };

  const resetForm = () => {
    setFormData({ lastName: '', email: '', adults: 1, children: 0 });
    reset();
  };

  // Capacity reached view
  const renderCapacityReached = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-burgundy/10 border-2 border-burgundy">
        <svg className="w-10 h-10 text-burgundy" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="font-serif text-3xl font-semibold text-navy-900 mb-3">
        Complet !
      </h3>
      <p className="font-body text-navy-600 mb-6 max-w-md mx-auto">
        La capacité maximale pour cet événement a été atteinte.
      </p>
      <p className="font-body text-navy-700 mb-8 max-w-md mx-auto">
        Pour être ajouté à la <strong>liste d'attente</strong>, veuillez nous contacter :
      </p>
      <a
        href="mailto:contact@ufe.ro?subject=Liste d'attente - Galette des Rois 2026"
        className="inline-flex items-center gap-2 px-6 py-3 bg-burgundy hover:bg-burgundy-dark text-cream font-serif font-medium transition-colors"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        Contacter l'UFE
      </a>
    </div>
  );

  // Success view with ticket details
  const renderSuccess = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto mb-8 flex items-center justify-center bg-gold/10 border-2 border-gold">
        <svg className="w-10 h-10 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="font-serif text-3xl font-semibold text-navy-900 mb-3">
        Inscription Réussie !
      </h3>
      {ticket && (
        <div className="bg-cream-warm border border-cream-dark p-6 mb-6 max-w-sm mx-auto">
          <p className="font-body text-xs text-navy-500 uppercase tracking-wider mb-2">
            Votre numéro de billet
          </p>
          <p className="font-serif text-2xl font-bold text-burgundy tracking-wider">
            {ticket.ticketNumber}
          </p>
        </div>
      )}
      <p className="font-body text-navy-600 mb-8 max-w-sm mx-auto">
        Nous avons hâte de vous retrouver le 10 Janvier.
        <br />
        <span className="text-navy-500">Un email de confirmation vous a été envoyé.</span>
      </p>
      <button
        onClick={resetForm}
        className="inline-flex items-center gap-2 font-serif font-medium text-burgundy hover:text-burgundy-dark transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Inscrire une autre famille
      </button>
    </div>
  );

  // Registration form
  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Error message */}
      {error && (
        <div className="flex gap-4 p-5 bg-burgundy/10 border-l-4 border-burgundy">
          <svg className="w-5 h-5 text-burgundy flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="font-body text-sm text-burgundy-dark leading-relaxed">
            {error.message}
          </p>
        </div>
      )}

      {/* Name and Email row */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block font-body text-sm font-semibold text-navy-800 mb-3 uppercase tracking-wide">
            Nom de Famille
          </label>
          <input
            type="text"
            required
            value={formData.lastName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, lastName: e.target.value }))
            }
            className="input-elegant"
            placeholder="ex: Dupont"
          />
        </div>
        <div>
          <label className="block font-body text-sm font-semibold text-navy-800 mb-3 uppercase tracking-wide">
            Email
          </label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            className="input-elegant"
            placeholder="votre@email.com"
          />
        </div>
      </div>

      {/* Attendee count */}
      <div>
        <label className="block font-body text-sm font-semibold text-navy-800 mb-5 uppercase tracking-wide text-center">
          Combien serez-vous ?
        </label>
        <div className="flex items-center justify-center gap-12 md:gap-16 py-6 bg-cream-warm border border-cream-dark">
          <div className="text-center">
            <span className="block font-body text-xs text-navy-600 uppercase tracking-wider mb-3">
              Adultes
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => adjustCount('adults', -1)}
                className="w-10 h-10 flex items-center justify-center border border-navy-300 text-navy-600 hover:bg-navy-900 hover:text-cream hover:border-navy-900 transition-all duration-300 font-serif text-xl"
              >
                -
              </button>
              <span className="font-serif text-3xl font-semibold text-navy-900 w-8 text-center">
                {formData.adults}
              </span>
              <button
                type="button"
                onClick={() => adjustCount('adults', 1)}
                className="w-10 h-10 flex items-center justify-center bg-navy-900 text-cream hover:bg-gold hover:text-navy-900 transition-all duration-300 font-serif text-xl"
              >
                +
              </button>
            </div>
          </div>

          <div className="w-px h-16 bg-cream-dark" />

          <div className="text-center">
            <span className="block font-body text-xs text-navy-600 uppercase tracking-wider mb-3">
              Enfants
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => adjustCount('children', -1)}
                className="w-10 h-10 flex items-center justify-center border border-navy-300 text-navy-600 hover:bg-navy-900 hover:text-cream hover:border-navy-900 transition-all duration-300 font-serif text-xl"
              >
                -
              </button>
              <span className="font-serif text-3xl font-semibold text-navy-900 w-8 text-center">
                {formData.children}
              </span>
              <button
                type="button"
                onClick={() => adjustCount('children', 1)}
                className="w-10 h-10 flex items-center justify-center bg-navy-900 text-cream hover:bg-gold hover:text-navy-900 transition-all duration-300 font-serif text-xl"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="flex gap-4 p-5 bg-navy-900/5 border-l-4 border-gold">
        <svg className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="font-body text-sm text-navy-700 leading-relaxed">
          L'entrée est libre sur inscription. Le paiement des parts de galette
          et des boissons se fera directement sur place au restaurant La Galette.
        </p>
      </div>

      {/* Submit button */}
      <button
        type="submit"
        disabled={isLoading}
        className="group relative w-full bg-burgundy hover:bg-burgundy-dark text-cream font-serif font-semibold text-lg py-5 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden"
      >
        <span className="relative z-10 flex items-center justify-center gap-3">
          {isLoading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Envoi en cours...</span>
            </>
          ) : (
            <>
              <span>Confirmer ma présence</span>
              <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </>
          )}
        </span>
        <div className="absolute inset-0 bg-gold-shimmer opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </button>

      <p className="text-center font-body text-xs text-navy-500">
        En cliquant, vous acceptez de recevoir les informations de l'UFE Roumanie.
      </p>
    </form>
  );

  // Loading state
  if (capacityLoading) {
    return (
      <section id="rsvp" className="py-24 lg:py-32 bg-burgundy relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-burgundy via-burgundy/95 to-burgundy-dark pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="rsvp" className="py-24 lg:py-32 bg-burgundy relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 text-burgundy-light/20 text-[10rem] hidden lg:block">
          <FontAwesomeIcon icon={faCrown} />
        </div>
        <div className="absolute bottom-10 right-10 text-burgundy-light/20 text-[8rem] transform rotate-180 hidden lg:block">
          <FontAwesomeIcon icon={faCrown} />
        </div>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-burgundy via-burgundy/95 to-burgundy-dark pointer-events-none" />

      <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-gold text-navy-900 font-body text-xs font-semibold uppercase tracking-[0.15em] mb-6">
            {isAtCapacity ? 'Complet' : 'Réservation Obligatoire'}
          </span>
          <h2 className="font-serif font-semibold text-4xl md:text-5xl text-cream mb-4">
            {isAtCapacity ? 'Événement Complet' : 'Réservez votre Galette'}
          </h2>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold/50" />
            <div className="crown-icon text-lg">
              <FontAwesomeIcon icon={faCrown} />
            </div>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold/50" />
          </div>
          {!isAtCapacity && (
            <p className="font-body text-lg text-cream/70 max-w-lg mx-auto">
              Afin de permettre au Chef de préparer le bon nombre de parts, merci de
              confirmer votre présence avant le 5 Janvier.
            </p>
          )}
        </div>

        {/* Form container */}
        <div className="relative">
          {/* Decorative frame */}
          <div className="absolute -inset-4 border border-gold/20 hidden md:block pointer-events-none" />

          <div className="bg-cream p-8 md:p-12 shadow-elegant-lg">
            {isAtCapacity ? renderCapacityReached() : isSuccess ? renderSuccess() : renderForm()}
          </div>
        </div>
      </div>
    </section>
  );
}
